// src/components/organization/UpgradePlan.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../context/AuthContext';
import { subscriptionApi } from '../../api/subscriptionApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmDialog from '../common/ConfirmDialog';
import PaymentMethodModal from '../subscription/PaymentMethodModal';
import {
    SparklesIcon, CheckCircleIcon, XCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// ==================== PIANI ====================
// =====================================================================
// PREZZI UFFICIALI — sincronizzati con Stripe e DB
// EUR/GBP | GNF (Guinée 1€≈11500) | XOF (CFA 1€≈655)
// Annuale = mensile × 10 (2 mesi gratis)
// =====================================================================
const PLANS_CONFIG = [
    {
        id: 'FREE',
        name: 'Free',
        icon: '🆓',
        color: { border: 'border-gray-200', badge: 'bg-gray-100 text-gray-600', btn: '' },
        prices: { EUR: { monthly: 0,       yearly: 0 },
                  GBP: { monthly: 0,       yearly: 0 },
                  GNF: { monthly: 0,       yearly: 0 },
                  XOF: { monthly: 0,       yearly: 0 } },
        features: [
            { label: '3 progetti attivi',   ok: true },
            { label: '2 utenti',            ok: true },
            { label: '100 spese/mese',      ok: true },
            { label: 'AI Assistant',        ok: false },
            { label: 'Report PDF',          ok: false },
            { label: 'API Access',          ok: false },
            { label: 'Multi-valuta',        ok: false },
        ],
    },
    {
        id: 'STARTER',
        name: 'Starter',
        icon: '🚀',
        color: { border: 'border-sky-400', badge: 'bg-sky-500 text-white', btn: 'bg-sky-500 hover:bg-sky-600' },
        prices: { EUR: { monthly: 29,      yearly: 290 },
                  GBP: { monthly: 25,      yearly: 250 },
                  GNF: { monthly: 330000,  yearly: 3300000 },
                  XOF: { monthly: 19000,   yearly: 190000 } },
        features: [
            { label: '10 progetti attivi',  ok: true },
            { label: '10 utenti',           ok: true },
            { label: '500 spese/mese',      ok: true },
            { label: '50 richieste AI/mese', ok: true },
            { label: 'Report PDF',          ok: true },
            { label: 'API Access',          ok: false },
            { label: 'Multi-valuta',        ok: false },
        ],
    },
    {
        id: 'PRO',
        name: 'Pro',
        icon: '⚡',
        popular: true,
        color: { border: 'border-blue-500', badge: 'bg-blue-600 text-white', btn: 'bg-blue-600 hover:bg-blue-700' },
        prices: { EUR: { monthly: 79,      yearly: 790 },
                  GBP: { monthly: 65,      yearly: 650 },
                  GNF: { monthly: 900000,  yearly: 9000000 },
                  XOF: { monthly: 52000,   yearly: 520000 } },
        features: [
            { label: 'Progetti illimitati', ok: true },
            { label: 'Utenti illimitati',   ok: true },
            { label: 'Spese illimitate',    ok: true },
            { label: '200 richieste AI/mese', ok: true },
            { label: 'Report avanzati',     ok: true },
            { label: 'API Access',          ok: true },
            { label: 'Multi-valuta',        ok: true },
        ],
    },
    {
        id: 'ENTERPRISE',
        name: 'Enterprise',
        icon: '👑',
        color: { border: 'border-purple-400', badge: 'bg-purple-700 text-white', btn: 'bg-purple-700 hover:bg-purple-800' },
        prices: { EUR: { monthly: 149,     yearly: 1490 },
                  GBP: { monthly: 125,     yearly: 1250 },
                  GNF: { monthly: 1700000, yearly: 17000000 },
                  XOF: { monthly: 98000,   yearly: 980000 } },
        features: [
            { label: 'Tutto di Pro +',      ok: true },
            { label: 'Storage 100 GB',      ok: true },
            { label: 'Supporto 24/7',       ok: true },
            { label: 'White Label',         ok: true },
            { label: 'Audit Logs',          ok: true },
            { label: 'SLA garantito',       ok: true },
            { label: 'Onboarding dedicato', ok: true },
        ],
    },
];

const CURRENCY_SYMBOL = { EUR: '€', GBP: '£', XOF: 'CFA ', GNF: 'FG ' };

const formatPrice = (amount, currency) => {
    const sym = CURRENCY_SYMBOL[currency] || '';
    if (currency === 'EUR' || currency === 'GBP') return `${sym}${amount}`;
    return `${sym}${amount.toLocaleString('fr-FR')}`;
};

// ==================== CARD SINGOLO PIANO ====================
const PlanCard = ({ plan, currency, billingCycle, isCurrent, onUpgrade, loading }) => {
    const cur = ['EUR','GBP','GNF','XOF'].includes(currency) ? currency : 'EUR';
    const price = plan.prices[cur]?.[billingCycle] ?? 0;

    const isFree = plan.id === 'FREE';

    return (
        <div className={`relative bg-white rounded-2xl border-2 ${isCurrent ? 'border-emerald-400' : plan.color.border} p-6 flex flex-col transition-shadow hover:shadow-md`}>
            {/* Badge popolare */}
            {plan.popular && !isCurrent && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className={`${plan.color.badge} text-xs font-bold px-3 py-1 rounded-full shadow`}>
                        CONSIGLIATO
                    </span>
                </div>
            )}
            {isCurrent && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                        Piano attuale
                    </span>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">{plan.icon}</span>
                <h3 className="text-base font-bold text-gray-900">{plan.name}</h3>
            </div>

            {/* Prezzo */}
            <div className="mb-5">
                {isFree ? (
                    <p className="text-3xl font-bold text-gray-900">Gratis</p>
                ) : (
                    <>
                        <span className="text-3xl font-bold text-gray-900">{formatPrice(price, currency)}</span>
                        <span className="text-gray-400 text-sm ml-1">/{billingCycle === 'yearly' ? 'anno' : 'mese'}</span>
                        {billingCycle === 'yearly' && (
                            <p className="text-emerald-600 text-xs font-semibold mt-1">🎉 2 mesi gratis!</p>
                        )}
                    </>
                )}
            </div>

            {/* Features */}
            <ul className="space-y-2 flex-1 mb-6">
                {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                        {f.ok
                            ? <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            : <XCircleIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        }
                        <span className={f.ok ? 'text-gray-700' : 'text-gray-400'}>{f.label}</span>
                    </li>
                ))}
            </ul>

            {/* Bottone */}
            {isCurrent ? (
                <div className="w-full py-2.5 bg-emerald-50 text-emerald-700 rounded-xl text-sm font-semibold text-center">
                    ✅ Attivo
                </div>
            ) : isFree ? (
                <div className="w-full py-2.5 bg-gray-100 text-gray-500 rounded-xl text-sm font-semibold text-center cursor-default">
                    Piano base
                </div>
            ) : (
                <button
                    onClick={() => onUpgrade(plan.id, billingCycle)}
                    disabled={loading}
                    className={`w-full py-2.5 ${plan.color.btn} text-white rounded-xl text-sm font-semibold transition disabled:opacity-50`}
                >
                    Passa a {plan.name}
                </button>
            )}
        </div>
    );
};

// ==================== COMPONENTE PRINCIPALE ====================
const UpgradePlan = () => {
    const { subscription, planLimits, cancelSubscription, loading } = useSubscription();
    const { organization } = useAuth();

    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [paymentModal, setPaymentModal] = useState({ open: false, plan: null });
    const [upgrading, setUpgrading] = useState(false);

    const plan        = subscription?.plan || 'FREE';
    const isSuperAdmin = subscription?.isSuperAdmin === true;
    const isTrialing  = subscription?.status === 'TRIALING';
    const trialDaysLeft = subscription?.trialEndsAt
        ? Math.max(0, Math.ceil((new Date(subscription.trialEndsAt) - Date.now()) / 86400000))
        : 0;

    const orgRegion   = organization?.region   || 'EUROPE';
    const orgCurrency = organization?.currency || 'EUR';

    const isPaid = ['PRO', 'ENTERPRISE'].includes(plan);

    const getModalAmount = () => {
        if (!paymentModal.plan) return 0;
        const p = PLANS_CONFIG.find(x => x.id === paymentModal.plan);
        if (!p) return 0;
        const cur = ['EUR','GBP','GNF','XOF'].includes(orgCurrency) ? orgCurrency : 'EUR';
        return p.prices[cur]?.[billingCycle] ?? 0;
    };

    const handleUpgradeClick = (planId) => {
        setPaymentModal({ open: true, plan: planId });
    };

    const handlePaymentConfirm = async ({ provider, phone }) => {
        setUpgrading(true);
        try {
            const res = await subscriptionApi.upgradeToPro({
                plan:         paymentModal.plan,
                billingCycle,
                provider,
                phone,
            });
            if (res.data?.paymentUrl) {
                if (res.data?.requiresPolling) {
                    toast.success('Richiesta inviata! Controlla il telefono per confermare.');
                    setPaymentModal({ open: false, plan: null });
                } else {
                    window.location.href = res.data.paymentUrl;
                }
            } else {
                toast.error('Errore avvio pagamento');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore durante il pagamento');
        } finally {
            setUpgrading(false);
        }
    };

    const features = planLimits ? [
        { name: planLimits.maxProjects >= 999999 ? 'Progetti illimitati' : `${planLimits.maxProjects} Progetti`, ok: true },
        { name: planLimits.maxUsers >= 999999 ? 'Utenti illimitati' : `${planLimits.maxUsers} Utenti`, ok: true },
        { name: planLimits.maxExpenses >= 999999 ? 'Spese illimitate' : `${planLimits.maxExpenses} Spese/mese`, ok: true },
        { name: 'AI Assistant',       ok: planLimits.aiEnabled },
        { name: 'Report PDF',         ok: planLimits.reportsEnabled },
        { name: 'API Access',         ok: planLimits.apiAccess },
        { name: 'Export PDF',         ok: planLimits.exportPdf },
        { name: 'Multi-valuta',       ok: planLimits.multiCurrency },
        { name: 'Supporto prioritario', ok: planLimits.prioritySupport },
    ] : [];

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">

            {/* Piano attuale */}
            <div className="bg-white rounded-2xl border p-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Piano Attuale</h2>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                plan === 'ENTERPRISE' ? 'bg-purple-100 text-purple-700'
                                : plan === 'PRO' ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>{plan}</span>
                            {isSuperAdmin && (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                    👑 SuperAdmin — Illimitato
                                </span>
                            )}
                            {isTrialing && trialDaysLeft > 0 && (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                                    🕐 Trial: {trialDaysLeft} giorni rimasti
                                </span>
                            )}
                            {subscription?.status === 'ACTIVE' && !isSuperAdmin && (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                                    ✓ Attivo
                                </span>
                            )}
                        </div>
                    </div>
                    {isPaid && !isTrialing && !isSuperAdmin && (
                        <button onClick={() => setShowCancelConfirm(true)} className="text-red-500 hover:text-red-700 text-sm">
                            Cancella abbonamento
                        </button>
                    )}
                </div>
                {features.length > 0 && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        {features.map((f, i) => (
                            <div key={i} className="flex items-center gap-2">
                                {f.ok
                                    ? <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    : <XCircleIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                }
                                <span className={`text-sm ${f.ok ? 'text-gray-700' : 'text-gray-400'}`}>{f.name}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Banner SuperAdmin */}
            {isSuperAdmin && (
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-6 text-white">
                    <div className="flex items-center gap-4">
                        <span className="text-5xl">👑</span>
                        <div>
                            <h3 className="text-xl font-bold">Accesso SuperAdmin Globale</h3>
                            <p className="text-yellow-100 mt-1 text-sm">Piano ENTERPRISE permanente con accesso illimitato.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Comparazione piani */}
            {!isSuperAdmin && (
                <div className="space-y-5">
                    {/* Toggle mensile/annuale */}
                    <div className="flex items-center justify-center gap-3">
                        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>
                            Mensile
                        </span>
                        <button
                            onClick={() => setBillingCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
                            className={`relative w-12 h-6 rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-blue-600' : 'bg-gray-200'}`}
                        >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
                            Annuale <span className="text-emerald-600 font-bold">-17%</span>
                        </span>
                    </div>

                    {/* Griglia 4 piani: FREE / STARTER / PRO / ENTERPRISE */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                        {PLANS_CONFIG.map(p => (
                            <PlanCard
                                key={p.id}
                                plan={p}
                                currency={orgCurrency}
                                billingCycle={billingCycle}
                                isCurrent={plan === p.id}
                                onUpgrade={handleUpgradeClick}
                                loading={upgrading}
                            />
                        ))}
                    </div>

                    {/* Provider pagamento accettati */}
                    <div className="bg-gray-50 rounded-2xl p-4 text-center">
                        <p className="text-xs text-gray-400">
                            Europa: 💳 <strong>Stripe</strong> · Africa: 🌊 <strong>Wave</strong> · 🟠 <strong>Orange Money</strong> · 🟡 <strong>MTN MoMo</strong>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">🔒 Pagamento sicuro SSL · Cancella quando vuoi</p>
                    </div>
                </div>
            )}

            {/* Modale pagamento */}
            <PaymentMethodModal
                isOpen={paymentModal.open}
                onClose={() => setPaymentModal({ open: false, plan: null })}
                onConfirm={handlePaymentConfirm}
                loading={upgrading}
                plan={paymentModal.plan}
                billingCycle={billingCycle}
                amount={getModalAmount()}
                currency={orgCurrency}
                region={orgRegion}
            />

            {/* Conferma cancellazione */}
            {showCancelConfirm && (
                <ConfirmDialog
                    title="Cancella abbonamento"
                    message="Sei sicuro? Tornerai al piano Free e perderai le funzionalità avanzate."
                    confirmText="Conferma cancellazione"
                    type="danger"
                    onConfirm={async () => { await cancelSubscription(); setShowCancelConfirm(false); }}
                    onCancel={() => setShowCancelConfirm(false)}
                />
            )}
        </div>
    );
};

export default UpgradePlan;
